/**
 * Default Menu Configurations per Role
 *
 * Extracted from the default (fallback) blocks in routes.js.
 * These serve as:
 *  1. The master list of all available menu items per role
 *  2. The fallback when no custom config exists in the database
 *  3. Seed data for the backend database
 */

const { ORGANIZATION_LABEL } = require("./components/bootstrap-components/GlobalVariables/globalvariable");

/**
 * Generate a stable ID for a menu item based on its route path.
 * For top-level items without a direct leaf route, uses a slugified name.
 */
function generateId(item, parentId) {
  const base = item.to && item.to !== "/components" && item.to !== "/"
    ? item.to.replace(/^\//, "").replace(/\//g, "_")
    : item.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  return parentId ? `${parentId}__${base}` : base;
}

/**
 * Convert a routes-style item to menu config format, recursively.
 */
function toMenuConfig(item, order, parentId) {
  const id = generateId(item, parentId);
  const config = {
    id,
    name: item.name,
    to: item.to || "",
    icon: item.icon || null,
    visible: true,
    order,
    exact: item.exact || false,
    border: item.border || false,
    badge: item.badge || null,
  };
  if (item.children && item.children.length > 0) {
    config.children = item.children.map((child, idx) => toMenuConfig(child, idx, id));
  }
  return config;
}

function getSuperAdminDefaults() {
  return [
    {
      name: "Home", to: "/", exact: true, icon: "home",
      children: [{ to: "/", name: "Dashboard", icon: "chart-pie", exact: true }],
    },
    {
      name: "Devices", to: "/components", icon: "tv",
      children: [
        { to: "/components/deviceList", name: "List" },
        { to: "/components/deviceAdd", name: "Add New" },
        { to: "/components/device/enquiry", name: "Inquiry" },
      ],
    },
    {
      name: "Bandwidth Report", to: "/components/bandwidthReport", icon: "wifi", exact: true,
    },
    {
      name: "Clients", to: "/components", icon: "user",
      children: [
        { to: "/components/clientList", name: "List" },
        { to: "/components/clientAdd", name: "Add New" },
      ],
    },
    {
      name: "Demand Manage", to: "/components", icon: "ad",
      children: [
        { to: "/components/demandSourceList", name: "Demand List" },
        { to: "/components/demandPartnerList", name: "Partner List" },
        { to: "/demand/mapping", name: "Demand Mapping" },
        { to: "/components/rtb/mapping", name: "RTB Mapping" },
        { to: "/offlineDemand/mapping", name: "Offline Demand Mapping" },
      ],
    },
    {
      name: "Video Campaigns", to: "/components", icon: "play",
      children: [
        { to: "/components/campaignList", name: "List" },
        { to: "/components/ActiveList", name: "Active Campaigns" },
        { to: "/components/campaignReport", name: "Report" },
        { to: "/components/combinedReport", name: "Granular Report" },
        {
          name: "Plan Search", to: "/components", icon: "search",
          children: [
            { to: "/components/planning/List", name: "Plan List" },
            { to: "/components/planning/planSearchNew", name: "Search plan" },
          ],
        },
        {
          name: "Audience Target", to: "/components", icon: "lock",
          children: [
            { to: "/components/campaign/audienceTarget/add", name: "Add" },
            { to: "/components/campaign/audienceTarget/list", name: "List" },
          ],
        },
        {
          name: "Advertiser", to: "/components", icon: "lock",
          children: [
            { to: "/components/CampaignPaymentAdmn", name: "Advertiser Payment" },
            { to: "/components/advertiserstatement", name: "Advertiser Statement" },
            { to: "/components/paymentReport", name: "Payment Details" },
          ],
        },
      ],
    },
    {
      name: "Creative Management", to: "/components", icon: "photo-video",
      children: [
        { to: "/components/creativeManageList", name: "List" },
        { to: "/components/creativeManageAdd", name: "Add New" },
      ],
    },
    { name: "Super Admin Report", to: "/components/superReport", icon: "user-tie" },
    { name: "Revenue Analytics", to: "/components/revenueanalyticsreport", icon: "file-invoice-dollar" },
    { name: "Merchant Statement", to: "/components/merchantStatement", icon: "copy" },
    { name: "Demand Report", to: "/components/demandReport", icon: "poll" },
    { name: "Demographic Report", to: "/components/demographic/report", icon: "chart-area" },
    {
      name: ORGANIZATION_LABEL || "Network", to: "/components", icon: "cubes",
      children: [
        { to: "/components/userNameList", name: "List" },
        { to: "/components/userNameAdd", name: "Add New" },
      ],
    },
    {
      name: "Locations", to: "/components", icon: "map-marked",
      children: [
        { to: "/components/locationList", name: "List" },
        { to: "/components/locationAdd", name: "Add New" },
      ],
    },
    {
      name: "User Manage", to: "/components", icon: "users-cog",
      children: [
        { to: "/components/userList", name: "List" },
        { to: "/components/userAdd", name: "Add User" },
      ],
    },
    { name: "Outage Report", to: "/components/outagereport", icon: "lock" },
    { name: "Health Report", to: "/components/healthreports", icon: "file-medical" },
    {
      name: "Template", to: "/components", icon: "copy",
      children: [
        { to: "/components/templateadd", name: "Template Add" },
        { to: "/components/templatelist", name: "Template List" },
        { to: "/components/createtheme", name: "Theme Create" },
        { to: "/components/themelist", name: "Theme List" },
        {
          name: "Banner", to: "/components", icon: "copy",
          children: [
            { to: "/components/bannerupload", name: "Banner Upload" },
            { to: "/components/bannerlist", name: "Banner List" },
          ],
        },
        {
          name: "RSS Feed", to: "/components", icon: "copy",
          children: [
            { to: "/components/rssfeedadd", name: "RSS Feed Add" },
            { to: "/components/rssfeedlist", name: "RSS Feed List" },
          ],
        },
      ],
    },
    { name: "Video Analytics Report", to: "/components/videoAnalyticsReport", icon: "poll" },
    { name: "Loop Analytics Report", to: "/components/doohAnalyticsReport", icon: "list" },
    { name: "Reconciliation", to: "/components/Reconciliation", icon: "balance-scale-left" },
    {
      name: "Service Configuration", to: "/components", icon: "cog",
      children: [{ to: "/components/ServiceConfiguration", name: "List" }],
    },
    {
      name: "Banner Campaigns", to: "/components", icon: "image",
      children: [
        {
          name: "Campaign Manage", to: "/components", icon: "play",
          children: [
            { to: "/components/displayCampaignList", name: "List" },
            { to: "/components/displayCampaign/Report", name: "Report" },
          ],
        },
        {
          name: "Demand Manage", to: "/components", icon: "image",
          children: [
            { to: "/components/displayDemandSourceList", name: "Demand List" },
            { to: "/displayDemand/mapping", name: "Demand Mapping" },
            { to: "/components/displayDemandReport", name: "Demand Report" },
            { to: "/components/displayrevenueanalyticsreport", name: "Revenue Analytics" },
          ],
        },
      ],
    },
    {
      name: "Integration", to: "/components", icon: "copy",
      children: [
        { to: "/components/integrationadd", name: "Integration Add" },
        { to: "/components/integrationlist", name: "Integration List" },
      ],
    },
    { name: "Intel Partner Details", to: "/components/Partnerdetails", icon: "poll" },
  ];
}


function getClientAdminDefaults() {
  return [
    {
      name: "Home", to: "/", exact: true, icon: "home",
      children: [{ to: "/", name: "Dashboard", icon: "chart-pie", exact: true }],
    },
    {
      name: "Devices", to: "/components", icon: "tv",
      children: [
        { to: "/components/deviceList", name: "List" },
        { to: "/components/deviceAdd", name: "Add New" },
        { to: "/components/device/enquiry", name: "Inquiry" },
      ],
    },
    {
      name: "Videos", to: "/components", icon: "video",
      children: [{ to: "/videos", name: "List" }],
    },
    {
      name: "Banner", to: "/components", icon: "image",
      children: [{ to: "/banners", name: "List" }],
    },
    {
      name: "Playlist", to: "/components", icon: "list",
      children: [
        { to: "/playlists", name: "List" },
        { to: "/playlists/add", name: "Add New" },
      ],
    },
    {
      name: "DOOH Loop", to: "/components", icon: "list-alt",
      children: [
        { to: "/dooh-loop/list", name: "List" },
        { to: "/dooh-loop/add", name: "Add" },
      ],
    },
    {
      name: "Video Campaigns", to: "/components", icon: "play",
      children: [
        { to: "/components/campaignList", name: "List" },
        { to: "/components/campaignReport", name: "Report" },
        {
          name: "Advertiser", to: "/components", icon: "lock",
          children: [
            { to: "/components/CampaignPaymentAdmn", name: "Advertiser Payment" },
            { to: "/components/advertiserstatement", name: "Advertiser Statement" },
            { to: "/components/paymentReport", name: "Payment Details" },
          ],
        },
      ],
    },
    {
      name: "Ad Confirmation", to: "/components", icon: "ad",
      children: [
        { to: "/components/pendingList", name: "Pending" },
        { to: "/components/approvedList", name: "Approved" },
        { to: "/components/rejectedList", name: "Declined Ads" },
        { to: "/components/AllAdList", name: "All Ads" },
      ],
    },
    { name: "Demand Ad Confirmation", to: "/components/demand/adapproval/list", icon: "ad" },
    {
      name: "Creative Management", to: "/components", icon: "photo-video",
      children: [
        { to: "/components/creativeManageList", name: "List" },
        { to: "/components/creativeManageAdd", name: "Add New" },
      ],
    },
    { name: "Revenue Report", to: "/components/superReport", icon: "user-tie" },
    { name: "Outage Report", to: "/components/outagereport", icon: "lock" },
    { name: "Merchant Statement", to: "/components/merchantStatement", icon: "copy" },
    { name: "Monthly Statement", to: "/components/monthlyStatement", icon: "copy" },
    {
      name: ORGANIZATION_LABEL || "Network", to: "/components", icon: "cubes",
      children: [
        { to: "/components/userNameList", name: "List" },
        { to: "/components/userNameAdd", name: "Add New" },
      ],
    },
    {
      name: "Locations", to: "/components", icon: "map-marked",
      children: [
        { to: "/components/locationList", name: "List" },
        { to: "/components/locationAdd", name: "Add New" },
      ],
    },
    {
      name: "User Manage", to: "/components", icon: "users-cog",
      children: [
        { to: "/components/userList", name: "List" },
        { to: "/components/userAdd", name: "Add User" },
      ],
    },
    {
      name: "Banner Campaigns", to: "/components", icon: "image",
      children: [
        {
          name: "Campaign Manage", to: "/components", icon: "play",
          children: [
            { to: "/components/displayCampaignList", name: "List" },
            { to: "/components/displayCampaign/Report", name: "Report" },
          ],
        },
      ],
    },
    { name: "Video Analytics Report", to: "/components/videoAnalyticsReport", icon: "poll" },
    { name: "Loop Analytics Report", to: "/components/doohAnalyticsReport", icon: "list" },
  ];
}

function getOrgManagerDefaults() {
  return [
    {
      name: "Home", to: "/", exact: true, icon: "home",
      children: [{ to: "/", name: "Dashboard", icon: "chart-pie", exact: true }],
    },
    {
      name: "Devices", to: "/components", icon: "tv",
      children: [
        { to: "/components/deviceList", name: "List" },
        { to: "/components/deviceAdd", name: "Add New" },
        { to: "/components/device/enquiry", name: "Inquiry" },

      ],
    },
    {
      name: "Videos", to: "/components", icon: "video",
      children: [{ to: "/videos", name: "List" }],
    },
    {
      name: "Banner", to: "/components", icon: "image",
      children: [{ to: "/banners", name: "List" }],
    },
    {
      name: "Playlist", to: "/components", icon: "list",
      children: [
        { to: "/playlists", name: "List" },
        { to: "/playlists/add", name: "Add New" },
      ],
    },
    {
      name: "Video Campaigns", to: "/components", icon: "play",
      children: [
        { to: "/components/campaignList", name: "List" }
      ],
    },
    {
      name: "DOOH Loop", to: "/components", icon: "list-alt",
      children: [
        { to: "/dooh-loop/list", name: "List" },
        { to: "/dooh-loop/add", name: "Add" },
      ],
    },
    {
      name: "Ad Confirmation", to: "/components", icon: "ad",
      children: [
        { to: "/components/pendingList", name: "Pending" },
        { to: "/components/approvedList", name: "Approved" },
        { to: "/components/rejectedList", name: "Declined Ads" },
        { to: "/components/AllAdList", name: "All Ads" },
      ],
    },
    { name: "Revenue Report", to: "/components/superReport", icon: "user-tie" },
    { name: "Monthly Statement", to: "/components/monthlyStatement", icon: "copy" },
    {
      name: "Locations", to: "/components", icon: "map-marked",
      children: [
        { to: "/components/locationList", name: "List" },
        { to: "/components/locationAdd", name: "Add New" },
      ],
    },
    {
      name: "User Manage", to: "/components", icon: "users-cog",
      children: [
        { to: "/components/userList", name: "List" },
        { to: "/components/userAdd", name: "Add User" },
      ],
    },
    { name: "Outage Report", to: "/components/outagereport", icon: "lock" },
    { name: "Video Analytics Report", to: "/components/videoAnalyticsReport", icon: "poll" },
    { name: "Loop Analytics Report", to: "/components/doohAnalyticsReport", icon: "list" },
  ];
}

// Role 12 - Location Manager
function getLocationManagerDefaults() {
  return [
    {
      name: "Home", to: "/", exact: true, icon: "home",
      children: [{ to: "/", name: "Dashboard", icon: "chart-pie", exact: true }],
    },
    {
      name: "Devices", to: "/components", icon: "tv",
      children: [
        { to: "/components/deviceList", name: "List" },
        { to: "/components/deviceAdd", name: "Add New" },
        { to: "/components/device/enquiry", name: "Inquiry" },

      ],
    },
    {
      name: "Videos", to: "/components", icon: "video",
      children: [{ to: "/videos", name: "List" }],
    },
    {
      name: "Banner", to: "/components", icon: "image",
      children: [{ to: "/banners", name: "List" }],
    },
    {
      name: "Playlist", to: "/components", icon: "list",
      children: [
        { to: "/playlists", name: "List" },
        { to: "/playlists/add", name: "Add New" },
      ],
    },
    {
      name: "DOOH Loop", to: "/components", icon: "list-alt",
      children: [
        { to: "/dooh-loop/list", name: "List" },
        { to: "/dooh-loop/add", name: "Add" },
      ],
    },
    { name: "Location Report", to: "/components/superReport", icon: "user-tie" },
  ];
}

// Role 24 - Advertiser
function getAdvertiserDefaults() {
  return [
    {
      name: "Home", to: "/", exact: true, icon: "home",
      children: [{ to: "/", name: "Dashboard", icon: "chart-pie", exact: true }],
    },
    {
      name: "Video Campaigns", to: "/components", icon: "play",
      children: [{ to: "/components/campaignReport", name: "Report" }],
    },
  ];
}

// Role 25 - Ad Ops
function getAdOpsDefaults() {
  return [
    {
      name: "Home", to: "/", exact: true, icon: "home",
      children: [{ to: "/", name: "Dashboard", icon: "chart-pie", exact: true }],
    },
    {
      name: "Locations", to: "/components", icon: "map-marked",
      children: [
        { to: "/components/locationList", name: "List" },
        { to: "/components/locationAdd", name: "Add New" },
      ],
    },
    {
      name: "Devices", to: "/components", icon: "tv",
      children: [
        { to: "/components/deviceList", name: "List" },
        { to: "/components/deviceAdd", name: "Add New" },
        { to: "/components/device/enquiry", name: "Inquiry" },

      ],
    },
    {
      name: "Demand Manage", to: "/components", icon: "ad",
      children: [
        { to: "/components/demandSourceList", name: "Demand List" },
        { to: "/components/demandPartnerList", name: "Partner List" },
        { to: "/demand/mapping", name: "Demand Mapping" },
        { to: "/components/rtb/mapping", name: "RTB Mapping" },
      ],
    },
    { name: "Super Admin Report", to: "/components/superReport", icon: "user-tie" },
    { name: "Demand Report", to: "/components/demandReport", icon: "poll" },
    { name: "Revenue Analytics", to: "/components/revenueanalyticsreport", icon: "file-invoice-dollar" },
    { name: "Outage Report", to: "/components/outagereport", icon: "lock" },
  ];
}

function getCampaignManagerDefaults() {
  return [
    {
      name: "Video Campaigns", to: "/components", icon: "play",
      children: [
        { to: "/components/campaignList", name: "List" },
        { to: "/components/campaignReport", name: "Report" },
      ],
    },
    {
      name: "Creative Management", to: "/components", icon: "photo-video",
      children: [
        { to: "/components/creativeManageList", name: "List" },
        { to: "/components/creativeManageAdd", name: "Add New" },
      ],
    },
  ];
}

function getTenantAdminDefaults() {
  return [
    {
      name: "Tenant", to: "/components", icon: "building",
      children: [
        { to: "/components/tenant/list", name: "List" },
        { to: "/components/tenant/add", name: "Add New" },
      ],
    },
  ];
}


function getDeviceAppAdminDefaults() {
  return [
    { name: "Device Add", to: "/components/deviceInstallAdd", icon: "tv" },
    { name: "Device List", to: "/components/deviceInstallList", icon: "tv" },
  ];
}

function getDeviceOrgManagerDefaults() {
  return [
    { name: "Device Add", to: "/components/deviceInstallAdd", icon: "tv" },
    { name: "Device List", to: "/components/deviceInstallList", icon: "tv" },
  ];
}



const DEFAULT_GENERATORS = {
  26: getSuperAdminDefaults,
  1: getClientAdminDefaults,
  10: getOrgManagerDefaults,
  12: getLocationManagerDefaults,
  24: getAdvertiserDefaults,
  25: getAdOpsDefaults,
  27: getCampaignManagerDefaults,
  30: getTenantAdminDefaults,
  31: getDeviceAppAdminDefaults,
  32: getDeviceOrgManagerDefaults,
};

/**
 * Get the default menu config for a given role in menu-config JSON format.
 * Returns an array of menu config items with id, visible, order fields.
 */
function getDefaultMenuConfig(roleId) {
  const generator = DEFAULT_GENERATORS[roleId];
  if (!generator) return [];
  const rawItems = generator();
  return rawItems.map((item, idx) => toMenuConfig(item, idx, null));
}

/**
 * Get the master list of ALL possible menu items for a role.
 * This is the same as getDefaultMenuConfig but explicitly named
 * to indicate it's the complete superset.
 */
function getMasterMenuItems(roleId) {
  return getDefaultMenuConfig(roleId);
}

/**
 * Get the list of role IDs that support menu customization.
 */
function getConfigurableRoles() {
  return [26, 1, 10, 12, 24, 25, 27];
}

module.exports = {
  getDefaultMenuConfig,
  getMasterMenuItems,
  getConfigurableRoles,
  default: DEFAULT_GENERATORS,
};
